import { Dropdown } from 'primereact/dropdown';
import React from 'react';
import { Chart } from 'react-google-charts';
import { BodySmallRegular, H6Emphasized, XStack, YStack } from 'smoothie';

import type { DbosTimelineBucket, TimelineInterval } from '../types/DbosTypes';
import { DBOS_STATUS_COLORS } from './dbosConstants';

interface DbosWorkflowTimelineProps {
    buckets: DbosTimelineBucket[];
    interval: TimelineInterval;
    onIntervalChange: (interval: TimelineInterval) => void;
    workflowNameFilter?: string;
    onWorkflowNameFilterChange?: (name: string | undefined) => void;
    availableWorkflowNames?: string[];
}

const INTERVAL_OPTIONS = [
    { label: '1 min', value: '1min' },
    { label: '5 min', value: '5min' },
    { label: '15 min', value: '15min' },
    { label: '30 min', value: '30min' },
    { label: '1 hour', value: 'hour' },
    { label: '1 day', value: 'day' },
];

export const DbosWorkflowTimeline = ({
    buckets,
    interval,
    onIntervalChange,
    workflowNameFilter,
    onWorkflowNameFilterChange,
    availableWorkflowNames,
}: DbosWorkflowTimelineProps) => {
    // Build chart data: [time, PENDING, SUCCESS, ERROR, MAX_RECOVERY, CANCELLED, ENQUEUED]
    const chartData = [
        [
            'Time',
            'Pending',
            'Success',
            'Error',
            'Max Recovery',
            'Cancelled',
            'Enqueued',
        ],
        ...buckets.map((bucket) => [
            new Date(bucket.time).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
            }),
            bucket.PENDING,
            bucket.SUCCESS,
            bucket.ERROR,
            bucket.MAX_RECOVERY_ATTEMPTS_EXCEEDED,
            bucket.CANCELLED,
            bucket.ENQUEUED,
        ]),
    ];

    const workflowNameOptions = [
        { label: 'All Workflows', value: undefined },
        ...(availableWorkflowNames ?? []).map((name) => ({
            label: name,
            value: name,
        })),
    ];

    const hasData = buckets.length > 0;

    return (
        <YStack gap={8} width="100%">
            <XStack
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <H6Emphasized>Workflow Volume Over Time</H6Emphasized>
                <XStack gap={8} alignItems="center">
                    {onWorkflowNameFilterChange != null &&
                        availableWorkflowNames != null &&
                        availableWorkflowNames.length > 0 && (
                            <Dropdown
                                value={workflowNameFilter}
                                options={workflowNameOptions}
                                onChange={(e) => {
                                    onWorkflowNameFilterChange(
                                        e.value as string | undefined,
                                    );
                                }}
                                placeholder="All Workflows"
                                style={{ fontSize: '0.8rem' }}
                            />
                        )}
                    <Dropdown
                        value={interval}
                        options={INTERVAL_OPTIONS}
                        onChange={(e) => {
                            onIntervalChange(e.value as TimelineInterval);
                        }}
                        style={{ fontSize: '0.8rem' }}
                    />
                </XStack>
            </XStack>

            {hasData ? (
                <Chart
                    chartType="ColumnChart"
                    width="100%"
                    height="300px"
                    data={chartData}
                    options={{
                        isStacked: true,
                        legend: { position: 'top', maxLines: 2 },
                        colors: [
                            DBOS_STATUS_COLORS.PENDING,
                            DBOS_STATUS_COLORS.SUCCESS,
                            DBOS_STATUS_COLORS.ERROR,
                            DBOS_STATUS_COLORS.MAX_RECOVERY_ATTEMPTS_EXCEEDED,
                            DBOS_STATUS_COLORS.CANCELLED,
                            DBOS_STATUS_COLORS.ENQUEUED,
                        ],
                        hAxis: {
                            title: 'Time',
                            slantedText: true,
                            slantedTextAngle: 45,
                        },
                        vAxis: {
                            title: 'Count',
                            minValue: 0,
                        },
                        chartArea: {
                            width: '85%',
                            height: '65%',
                        },
                        bar: { groupWidth: '80%' },
                    }}
                />
            ) : (
                <YStack
                    alignItems="center"
                    justifyContent="center"
                    height={200}
                >
                    <BodySmallRegular color="$gray1000">
                        No workflow data for this time range
                    </BodySmallRegular>
                </YStack>
            )}
        </YStack>
    );
};
